import {
    LayoutDashboard,
    FileBarChart,
    Wrench,
    Warehouse,
    Cpu,
    Trophy,
    User,
    Users,
    Package,
    X,
    Settings,
    LogOut,
    RefreshCw,
    ArrowUpRight,
    LifeBuoy,
    Moon,
    Sun,
} from 'lucide-react';
import { motion } from 'framer-motion';

import NavItem from './NavItem';
import { useAuth } from '@/auth/AuthContext';
import { useTheme } from '@/theme/ThemeContext';

interface Props {
    isMobile?: boolean;
    onClose?: () => void;
}

const Sidebar = ({
                     isMobile = false,
                     onClose,
                 }: Props) => {
    const {
        user,
        logout,
        changeSecretaryContext,
    } = useAuth();
    const { isDark, toggleTheme } = useTheme();

    /*
     * No desktop, onClose não existe e o menu permanece aberto.
     * No mobile, fecha o menu depois de selecionar uma página.
     */
    const handleNavItemClick = () => {
        if (isMobile) {
            onClose?.();
        }
    };

    const hasMultipleSecretaries =
        Boolean(user?.secretaries && user.secretaries.length > 1);

    const role = user?.role;

    const canManageSupport = [
        'ADMIN',
        'TECHNICAL_ADMIN',
        'TECHNICAL',
    ].includes(role || '');

    const canManageStock = [
        'ADMIN',
        'TECHNICAL_ADMIN',
        'TECHNICAL',
    ].includes(role || '');

    return (
        <motion.aside
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
            className="app-sidebar flex h-full w-[260px] select-none flex-col justify-between overflow-hidden border-r border-white/5 bg-[#111827]/95 shadow-2xl shadow-black/20 backdrop-blur-xl"
        >
            {/* Área superior */}
            <div className="flex flex-1 flex-col overflow-y-auto custom-scrollbar">
                {/* Cabeçalho */}
                <div className="flex h-16 shrink-0 items-center justify-between px-4">
                    <div className="flex items-center gap-3">
                        <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-600/25">
                            <span className="absolute inset-0 animate-ping rounded-xl bg-blue-500/20" />
                            <Package size={18} className="relative" />
                        </span>
                        <div>
                            <span className="block text-[11px] font-black uppercase italic tracking-[0.18em] text-blue-500">
                                STOCK<span className="text-white">_SYS</span>
                            </span>
                            <span className="block text-[8px] font-bold uppercase tracking-[0.2em] text-slate-600">Gestão municipal</span>
                        </div>
                    </div>

                    {/* Fechar aparece somente no mobile */}
                    {isMobile && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
                            title="Fechar menu"
                            aria-label="Fechar menu"
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>

                {/* Navegação */}
                <nav className="space-y-6 px-2 pb-4">
                    {/* Geral */}
                    <div className="space-y-1">
                        <span className="mb-2 block px-3 text-[9px] font-black uppercase tracking-widest text-slate-600">
                            Geral
                        </span>

                        {role !== 'VIEWER' && <NavItem
                            to="/dashboard"
                            icon={LayoutDashboard}
                            label="Dashboard"
                            collapsed={false}
                            onClick={handleNavItemClick}
                        />}

                        {role && <NavItem to="/reports" icon={FileBarChart} label="Relatórios" collapsed={false} onClick={handleNavItemClick} />}

                        {role !== 'VIEWER' && <NavItem
                            to="/ranking"
                            icon={Trophy}
                            label="Ranking"
                            collapsed={false}
                            onClick={handleNavItemClick}
                        />}
                    </div>

                    {/* Estoque e distribuição */}
                    {canManageStock && (
                        <div className="space-y-1">
                            <span className="mb-2 block px-3 text-[9px] font-black uppercase tracking-widest text-slate-600">
                                Estoque & Distribuição
                            </span>

                            <NavItem
                                to="/core-stock-manager"
                                icon={Package}
                                label="Estoque Central"
                                collapsed={false}
                                onClick={handleNavItemClick}
                            />

                            <NavItem
                                to="/stock-outflow"
                                icon={ArrowUpRight}
                                label="Saída de materiais"
                                collapsed={false}
                                onClick={handleNavItemClick}
                            />
                        </div>
                    )}

                    {/* Ativos e manutenção */}
                    {canManageSupport && (
                        <div className="space-y-1">
                            <span className="mb-2 block px-3 text-[9px] font-black uppercase tracking-widest text-slate-600">
                                Ativos & Manutenção
                            </span>

                            <NavItem to="/external-repairs" icon={Wrench} label="Manutenção externa" collapsed={false} onClick={handleNavItemClick} />
                            <NavItem
                                to="/asset-composer"
                                icon={Cpu}
                                label="Cad. Equipamentos"
                                collapsed={false}
                                onClick={handleNavItemClick}
                            />
                        </div>
                    )}

                    {/* Atendimento */}
                    {canManageSupport && (
                        <div className="space-y-1">
                            <span className="mb-2 block px-3 text-[9px] font-black uppercase tracking-widest text-slate-600">
                                Atendimento
                            </span>

                            <NavItem
                                to="/tickets"
                                icon={LifeBuoy}
                                label="Chamados"
                                collapsed={false}
                                onClick={handleNavItemClick}
                            />


                            <NavItem
                                to="/applicants"
                                icon={Users}
                                label="Solicitantes"
                                collapsed={false}
                                onClick={handleNavItemClick}
                            />
                        </div>
                    )}

                    {/* Sistema */}
                    <div className="space-y-1 border-t border-white/5 pt-2">
                        {role === 'ADMIN' && (
                            <>
                            <NavItem to="/organization" icon={Users} label="Equipe/Organização" collapsed={false} onClick={handleNavItemClick} />
                            <NavItem to="/inventory-centers" icon={Warehouse} label="Centrais de estoque" collapsed={false} onClick={handleNavItemClick} />
                            <NavItem to="/users" icon={Users} label="Usuários" collapsed={false} onClick={handleNavItemClick} />
                            <NavItem
                                to="/system-settings"
                                icon={Settings}
                                label="Configurações"
                                collapsed={false}
                                onClick={handleNavItemClick}
                            />
                            </>
                        )}

                        {role !== 'VIEWER' && <NavItem
                            to="/profile"
                            icon={User}
                            label="Perfil"
                            collapsed={false}
                            onClick={handleNavItemClick}
                        />}
                    </div>
                </nav>
            </div>

            {/* Rodapé */}
            <div className="app-sidebar-footer shrink-0 space-y-1 border-t border-white/5 bg-[#0f1523] p-2">
                {user && (
                    <div className="truncate px-3 py-2 text-xs text-slate-400">
                        <span className="block text-[10px] font-bold uppercase text-slate-500">
                            Servidor
                        </span>

                        <span className="block truncate font-semibold text-slate-200">
                            {user.name}
                        </span>
                    </div>
                )}

                {hasMultipleSecretaries && (
                    <button
                        type="button"
                        onClick={changeSecretaryContext}
                        className="flex w-full items-center gap-3 rounded-xl p-2 text-xs font-bold text-slate-400 transition-colors hover:bg-amber-500/10 hover:text-amber-400"
                        title="Trocar equipe ou secretaria"
                    >
                        <RefreshCw size={16} className="shrink-0" />
                        <span>Trocar Acesso</span>
                    </button>
                )}

                <button
                    type="button"
                    onClick={toggleTheme}
                    className="theme-toggle flex w-full items-center gap-3 rounded-xl p-2 text-xs font-bold text-slate-400 transition-all hover:bg-blue-500/10 hover:text-blue-400"
                    title={isDark ? 'Ativar tema claro' : 'Ativar tema escuro'}
                    aria-label={isDark ? 'Ativar tema claro' : 'Ativar tema escuro'}
                    aria-pressed={!isDark}
                >
                    <motion.span
                        key={isDark ? 'sun' : 'moon'}
                        initial={{ opacity: 0, rotate: -35, scale: 0.75 }}
                        animate={{ opacity: 1, rotate: 0, scale: 1 }}
                        transition={{ duration: 0.22 }}
                        className="shrink-0"
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </motion.span>
                    <span>{isDark ? 'Tema claro' : 'Tema escuro'}</span>
                </button>

                <button
                    type="button"
                    onClick={logout}
                    className="flex w-full items-center gap-3 rounded-xl p-2 text-xs font-bold text-slate-400 transition-colors hover:bg-rose-500/10 hover:text-rose-400"
                    title="Sair do Sistema"
                >
                    <LogOut size={16} className="shrink-0" />
                    <span>Sair</span>
                </button>
            </div>
        </motion.aside>
    );
};

export default Sidebar;
